import { Selectors } from 'Reducers'
import { Store } from 'redux'
import { ONBOARDING_STEPS } from 'types/onboardingTypes'
import { axios } from 'Fetcher'
import { getCookiesMap, getLocalId, getLowercaseSearchParams } from 'Lib'
import { v4 as uuidv4 } from 'uuid'
import { SHAHashValue } from 'Lib/Crypto'

const DISABLE_ON_DEV = true // Set to false to enable Snapchat in development

type AnyFunction = (...args: any[]) => any

interface CustomDataParams {
  currency?: string
  value?: number
  item_ids?: string[]
  transaction_id?: string
  order_id?: string
  payment_info_available?: number
  success?: number
  [key: string]: any
}

interface SnapCAPIEventIdentifiers {
  // This should be any unique value enough for Snapchat to identify the event on both Pixel and CAPI and dedupe it if needed
  id: string
  name: string
}

interface EventUserData {
  user?: IUser
  trackedEmail?: string
  gender?: IGender | string | null
}

interface SnapCAPIEventData {
  event_name: string
  event_time: number
  event_id?: string
  action_source: 'WEB' | 'MOBILE_APP'
  event_source_url?: string
  user_data: {
    em?: Array<string | undefined> | string // email
    ph?: Array<string | undefined> | string // phone
    fn?: Array<string | undefined> | string // first name
    ln?: Array<string | undefined> | string // last name
    ge?: Array<string | undefined> | string // gender
    ct?: Array<string | undefined> | string // city
    st?: Array<string | undefined> | string // state
    zp?: Array<string | undefined> | string // zip
    country?: Array<string | undefined> | string // country
    external_id?: Array<string | undefined> | string // external id, e.g. bodyfast user id
    client_ip_address?: string
    client_user_agent?: string
    sc_click_id?: string
    click_id?: string
    sc_cookie1?: string
  }
  custom_data: CustomDataParams
  app_data?: {
    advertiser_tracking_enabled?: boolean
    extinfo?: Array<string | undefined>
    app_id?: string
  }
}

const SNAPCHAT_PIXEL_ID = '935a4969-b143-408d-b279-c4a3d351d425'
const SNAPCHAT_SCRIPT_ID = 'snap-pixel-code'
const SAPI_ROUTING_PATH = '/api/collect/sapi'

if (!window.snaptr) {
  const snaptr = function (...args: any[]) {
    if ((snaptr as any).handleRequest) {
      (snaptr as any).handleRequest(...args)
    } else {
      snaptr.queue.push(args)
    }
  }

  const initialQueue: any[] = []
  snaptr.queue = initialQueue
  window.snaptr = snaptr
}

let cookiesGranted = false
const queuedEvents = new Map<string, { event: SnapCAPIEventIdentifiers, data: Partial<SnapCAPIEventData> }>()

const getSnapchatClickId = (): string | null => {
  const originalSearchParams = new URLSearchParams((localStorage.getItem('originalSearchParams') || '').toLowerCase())
  return originalSearchParams.get('sccid') ?? getLowercaseSearchParams().get('sccid')
}

const insertSnapchatPixelScript = () => {
  if (document.getElementById(SNAPCHAT_SCRIPT_ID)) {
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://sc-static.net/scevent.min.js'
  script.id = SNAPCHAT_SCRIPT_ID

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  }
}

const logConversionsEvent = async (event: SnapCAPIEventIdentifiers, data: Partial<SnapCAPIEventData> = {}) => {
  const clickId = getSnapchatClickId()
  const cookie1 = getCookiesMap().get('_scid')
  const hashedLocalId = await SHAHashValue(getLocalId())

  await axios.post(SAPI_ROUTING_PATH, {
    ...data,
    action_source: 'WEB',
    event_name: event.name,
    event_id: event.id,
    event_time: Math.floor(Date.now() / 1000),
    user_data: {
      ...data.user_data,
      // Both `sc_click_id` and `click_id` seem to be required by the Snapchat API and are referenced
      // interchangeably in their docs, both fields must be set to the same value if present.
      // See both: https://businesshelp.snapchat.com/s/article/troubleshooting-click-id?language=en_GB
      // and https://businesshelp.snapchat.com/s/article/events-faq?language=en_GB
      ...(clickId && { sc_click_id: clickId, click_id: clickId }),
      ...(cookie1 && {
        sc_cookie1: cookie1
      }),
      external_id: hashedLocalId ? [hashedLocalId] : undefined,
      client_user_agent: navigator.userAgent
    }
  })
}

// Forward queued events to CAPI when initialized
const executeQueuedConversionsEvents = async (): Promise<boolean[] | null> => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log('Snapchat Pixel initialized - skipping queued events execution in dev mode', queuedEvents)
    return null
  }

  return await Promise.all(
    Array
      .from(queuedEvents.entries())
      .map(async ([eventId, { event, data }]) => {
        try {
          await logConversionsEvent(event, data)
          queuedEvents.delete(eventId)
          return true
        } catch (error) {
          logError('web_snapchat_SAPIQueuedEventError', error?.response?.data ?? error?.message ?? error, {}, true)
          return false
        }
      })
  )
}

const trackEvent = async (event: string, data: Record<string, any> = {}, state: EventUserData = {}) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log(`Tracking Snapchat Pixel event: ${event}`, data)
    return
  }

  const hashedEmail = await SHAHashValue(state.trackedEmail)
  const hashedName = await SHAHashValue(state.user?.personal?.name)
  const hashedGender = await SHAHashValue(state.user?.personal?.gender)
  const eventData = {
    event: {
      id: uuidv4(),
      name: event
    },
    metadata: {
      event_source_url: window.location.href,
      user_data: {
        // Additional user data can be added here if needed
        em: hashedEmail ? [hashedEmail] : undefined,
        fn: hashedName ? [hashedName] : undefined,
        ge: hashedGender ? [hashedGender] : undefined
      },
      custom_data: data as CustomDataParams
    }
  }

  // We should still track the event via Pixel regardless of the pixel being initialized or not
  // to track events happening before user consent or pixel initialization
  window?.snaptr?.('track', eventData.event.name, { ...data, client_dedup_id: eventData.event.id })

  if (!cookiesGranted) {
    queuedEvents.set(eventData.event.id, { event: eventData.event, data: eventData.metadata })
    return
  }

  try {
    await logConversionsEvent(eventData.event, eventData.metadata)
  } catch (error) {
    // Catch block should not throw to avoid breaking main flow when CAPI fails
    logError(
      'web_snapchat_SAPIError',
      new Error(`Snapchat CAPI event logging failed: ${error?.message}`),
      error?.response?.data ?? error?.message ?? error,
      true
    )
  }
}

const pageView = async (state: EventUserData = {}) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log('Tracking Snapchat Pixel page view')
    return
  }

  await trackEvent('PAGE_VIEW', {}, state)
}

const revokeConsent = () => {
  cookiesGranted = false

  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  const existingScript = document.querySelector(`script#${SNAPCHAT_SCRIPT_ID}`)

  if (existingScript) {
    existingScript.remove() // Remove script from DOM
  }
}

export function createSnapchatService(store: Store) {
  let currentEmail: string | null = null

  const initializePixel = (store: Store) => {
    try {
      // The script will only be inserted once into the DOM
      insertSnapchatPixelScript()

      if (__DEV__ && DISABLE_ON_DEV) {
        console.log('Initializing Snapchat Pixel')
        return
      }

      const state = store.getState()
      const trackedEmail = Selectors.getTrackedEmail(state)
      const clickId = getSnapchatClickId()

      if (trackedEmail) {
        currentEmail = trackedEmail
        window.snaptr('init', SNAPCHAT_PIXEL_ID, { user_email: trackedEmail, sc_click_id: clickId })
        return
      }

      window.snaptr('init', SNAPCHAT_PIXEL_ID, { sc_click_id: clickId })
    } catch (err) {
      logError('web_snapchat_initError', err, {}, true)
    } finally {
      cookiesGranted = true
      void executeQueuedConversionsEvents()
    }
  }

  const setAdvancedMatchingEmail = (email: string) => {
    if (!(__DEV__ && DISABLE_ON_DEV) &&
      window.snaptr &&
      currentEmail !== email) {
      currentEmail = email

      const clickId = getSnapchatClickId()
      window.snaptr('init', SNAPCHAT_PIXEL_ID, { user_email: email, sc_click_id: clickId })
    }
  }

  const withStoredUserState = <Func extends AnyFunction>(
    fn: Func,
  ) => {
    return (...args: Parameters<Func>) => {
      const state = store.getState()
      const email = Selectors.getEmail(state)
      const onboardingEmail = Selectors.getOnboardingEmail(state)
      const user = Selectors.getUser(state)
      const gender = Selectors.getGender(state)

      const trackedEmail = user?.providerData?.email || email || onboardingEmail

      if (trackedEmail) {
        setAdvancedMatchingEmail(trackedEmail)
      }

      // Call the original function with user data appended to its arguments
      fn(...args, {
        user,
        trackedEmail,
        gender
      })
    }
  }

  return {
    initializePixel: () => initializePixel(store),
    logEvent: withStoredUserState(trackEvent),
    pageView: withStoredUserState(pageView),
    revokeConsent
  }
}

export const SNAPCHAT_CUSTOM_EVENTS: Partial<Record<ONBOARDING_STEPS, string>> = {
  gender: 'CUSTOM_EVENT_1',
  reinforcement_scientific: 'CUSTOM_EVENT_2',
  bmi: 'CUSTOM_EVENT_3',
  email: 'CUSTOM_EVENT_4',
  receive_email: 'CUSTOM_EVENT_5'
}

export type ISnapchatService = ReturnType<typeof createSnapchatService>
